import { css } from '@lastbrand/web-ui-styled-system/css'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

// Panda twin of BundleProductPurchase.module.scss — 1:1 visual translation of the shipped
// `_newDesignV2` / `_updatedDesignV2` state (the only variants still rendered; legacy fluid-rem
// values converted at a 10px root).

const bundlesImagesAndPurchaseRaw = css.raw({
  display: { base: 'block', lg: 'grid' },
  maxWidth: '1600px',
  marginInline: { base: 'unset', lg: 'auto' },
  paddingInline: { base: '0', lg: 'space-48' },
  columnGap: { base: '0', lg: 'space-120' },
  gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 4fr)',
  marginBottom: { base: 'space-40', lg: 0 },
  // Same STRFRNT-16252 standard hero bottom padding the non-bundle DS hero carries
  // (`dsHeroRowFrame`): the section below (Shop the Look) owns no top gap of its own on the
  // non-PL surface, so without this the bundle hero sat flush against it.
  paddingBottom: { base: '0', lg: 'space-48' },
})

export const bundlesImagesAndPurchaseCss = css(bundlesImagesAndPurchaseRaw)

// PL (`hideBundleImages`) twin of the legacy host overrides in
// `src/components/widgets/bundle-purchase/bundle-purchase.module.scss`: with the gallery column
// gone the section is the lone buy box inside the PL PRODUCT_INFO column, so the desktop grid
// collapses to a single full-width block (`flow-root`, not `block`, so a child's top margin can't
// collapse through) and the full-bleed side padding goes. Merged via one `css()` call — a second
// class through `cx()` would not override deterministically.
export const bundlesImagesAndPurchaseNoImagesCss = css(bundlesImagesAndPurchaseRaw, {
  display: { lg: 'flow-root' },
  paddingInline: { lg: '0' },
  // PL twin of `dsHeroRowFrameLgSeamOwnedClass`: the gap below the hero is owned by the
  // page-level seam margin on the first divider section, so the hero itself carries none.
  paddingBottom: { lg: '0' },
})

// The gallery column's sticky-travel shell (same trap `product-overview/index.tsx` documents for
// PL): the hero grid stretches this cell to the full row height, but the DS gallery block inside
// a plain block wrapper would not stretch with it — the shell must be a flex container so the
// gallery fills the row and its OWN `position: sticky` root (top 176px/6px pin, clamp freeze —
// `product-gallery-ds`) has room to travel. This replaced a bundle-local sticky wrapper whose
// legacy offsets (78px, the 120px legacy header) double-stickied the DS gallery and pinned it
// wrong under the 170px DS header (STRFRNT-16460).
export const bundleImagesWrapperCss = css({
  position: 'relative',
  lg: {
    display: 'flex',
    '& > *': { flex: '1 1 auto', minWidth: 0 },
  },
})

const bundlesPurchaseContainerRaw = css.raw({
  marginTop: { base: 'space-12', lg: 'space-48' },
  // 480px is a max-width cap only: the column sits in a fractional grid cell, so width stays
  // fluid and shrinks with the cell on narrower desktops instead of overflowing it.
  maxWidth: { lg: '480px' },
  width: { base: 'calc(100% - var(--web-ui-spacing-space-32))', lg: '100%' },
  marginInline: { base: 'auto', lg: 'unset' },
})

export const bundlesPurchaseContainerCss = css(bundlesPurchaseContainerRaw)

// PL (`hideBundleImages`) twin of the legacy STRFRNT-16313 fix: the desktop space-48 top margin
// top-aligns the title with the inline gallery image on non-PL; in PL the gallery is its own
// widget, so the same margin just pushes the buy box down. `'0'` is a literal — `space-0` is not
// a Panda token and would fail silently. Mobile keeps its space-12 gap.
export const bundlesPurchaseContainerNoImagesCss = css(bundlesPurchaseContainerRaw, {
  marginTop: { lg: '0' },
})

export const headerTextCss = css({
  width: 'full',
})

// The bundle price row itself renders through the ProductPriceDS block (variant="bundle");
// this wrapper only positions it.
export const bundlePriceCss = css({
  marginTop: { base: '-space-8', lg: 'space-20' },
  marginBottom: 'space-24',
})

export const bundleDescriptionCss = css({
  textStyle: { base: 'body-12', lg: 'body-14' }, // exact token match for legacy 12/16 and 14/20
  marginBottom: { base: 'space-32', lg: 'space-40' }, // legacy 34px -> nearest token space-32
  color: 'black',
  width: { base: 'full', lg: 'auto' },
})

export const addOnBundleProductsWrapperCss = css({
  borderTop: '1px solid',
  borderColor: 'lightgray',
})

export const addOnBundleProductsDiscountTextCss = css({
  display: 'flex',
  alignItems: 'center',
  // title-12 = 12px/16px/500, 0.05em tracking (0.6px @12px), uppercase - exact legacy match
  textStyle: 'title-12',
  color: 'green-100',
  // legacy: margin 3rem 0 + _newDesignV2 margin-bottom 1.6rem -> top 30px / bottom 16px
  paddingTop: 'space-32',
  paddingBottom: 'space-16',
})

export const addOnBundleProductsDiscountSvgCss = css({
  height: '11px',
  width: '11px',
  marginRight: 'space-8',
})

export const cartButtonWrapperCss = css({
  scrollMargin: 'space-40',
})

export const cartButtonWrapperWithExpressPayCss = css({
  display: 'flex',
  flexDirection: 'row-reverse',
  gap: 'space-12',
  width: 'full',
  alignItems: 'center',
})

// Passed to web-ui Button's `css` prop, which takes a raw SystemStyleObject, not a compiled class string.
export const cartButtonStyle: SystemStyleObject = {
  width: 'full',
  height: '50px', // component dimension from the legacy CTA spec; not on the spacing scale
  // title-14 = 14px/20px/500, 0.05em tracking, uppercase - matches the legacy CTA typography
  textStyle: 'title-14',
  textAlign: 'center',
  marginTop: { base: 'space-2', lg: 0 },
  lg: {
    _hover: { backgroundColor: 'black', color: 'white' },
  },
}

export const cartButtonPointerEventsNoneStyle: SystemStyleObject = {
  pointerEvents: 'none',
}

export const cartButtonWithExpressPayCss = css({
  width: 'full',
  padding: 0,
  height: '50px',
})

export const applePayButtonCss = css({
  marginTop: 'space-12',
})
